
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Voucher } from '@/types/voucher';

// Define a type for RPC parameters
type RPCParams = {
  p_campaign_id?: string;
  p_user_id?: string;
  p_voucher_id?: string;
  [key: string]: any;
};

export function useUserVouchers() {
  const { user } = useAuth();
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVouchers = async () => {
    if (!user) {
      setVouchers([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('vouchers')
        .select(`
          *,
          campaign:campaigns(
            *,
            venue:venues(*)
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      // Convert the Supabase voucher format to the app's Voucher type
      const formattedVouchers: Voucher[] = (data as any[]).map(v => ({
        id: v.id,
        code: v.code,
        title: v.campaign.title,
        description: v.campaign.description || '',
        venue: {
          id: v.campaign.venue?.id,
          name: v.campaign.venue?.name || 'Unknown Venue',
          address: v.campaign.venue?.address || '',
          city: v.campaign.venue?.city || '',
          state: v.campaign.venue?.state || '',
          zip: v.campaign.venue?.zip || ''
        },
        sponsorName: v.campaign?.sponsor_name || 'BeerDrop',
        sponsorMessage: v.campaign?.sponsor_message || 'Enjoy your free beer!',
        image: v.campaign?.voucher_image || '/placeholder.svg',
        claimedAt: v.claimed_at || new Date().toISOString(),
        expiresAt: v.campaign?.end_date || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        isRedeemed: v.status === 'redeemed',
        redeemedAt: v.redeemed_at || undefined
      }));

      setVouchers(formattedVouchers);
    } catch (err: any) {
      console.error('Error fetching vouchers:', err);
      setError(err.message || 'Failed to fetch vouchers');
      toast.error('Failed to load your vouchers');
    } finally {
      setLoading(false);
    }
  };

  const claimVoucher = async (code: string) => {
    if (!user) {
      toast.error('You must be logged in to claim a voucher');
      return { success: false };
    }

    try {
      setLoading(true);

      // Call the RPC function to claim a voucher
      const params: RPCParams = {
        p_campaign_id: code,
        p_user_id: user.id
      };
      
      const { data, error } = await supabase.rpc('claim_voucher', params);

      if (error) {
        throw error;
      }

      if (!data) {
        throw new Error('No vouchers available for this campaign');
      }

      toast.success('Voucher claimed successfully!');
      await fetchVouchers(); // Refresh the vouchers list
      return { success: true, voucherId: data };
    } catch (err: any) {
      console.error('Error claiming voucher:', err);
      toast.error(err.message || 'Failed to claim voucher');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const redeemVoucher = async (voucherId: string) => {
    if (!user) {
      toast.error('You must be logged in to redeem a voucher');
      return { success: false };
    }

    try {
      setLoading(true);

      // Call the RPC function to redeem a voucher
      const params: RPCParams = {
        p_voucher_id: voucherId
      };
      
      const { data, error } = await supabase.rpc('update_voucher_redemption', params);

      if (error) {
        throw error;
      }

      toast.success('Voucher redeemed successfully!');
      await fetchVouchers(); // Refresh the vouchers list
      return { success: true };
    } catch (err: any) {
      console.error('Error redeeming voucher:', err);
      toast.error(err.message || 'Failed to redeem voucher');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVouchers();
  }, [user]);

  return {
    vouchers,
    loading,
    error,
    fetchVouchers,
    claimVoucher,
    redeemVoucher,
  };
}
